import {
  EntityContext,
  findNode,
  FormNode,
  Message,
  readMessageModelByCode,
  ValidationMessageGroup,
  ValidationMessageParameters,
  ValidationNode,
} from '@msdk/definitions'

export function applyServerMessages(
  form: FormNode | undefined,
  messages: ValidationMessageGroup[],
  context: EntityContext,
) {
  const messageEntities = messages ?? []
  messageEntities.forEach((e) => {
    const alias = context.getAliasFromEntityId(e.id)
    if (alias === undefined) {
      return
    }

    const entityFields = Object.keys(e.items)
    entityFields.forEach((f) => {
      const fieldName = alias != '' ? `${alias}.${f}` : f
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      applyValidationNode(findNode(form, fieldName), e.items[f])
    })
  })
}

/**
 * The server validation group of a list field holds one group per row instead
 * of the direct messages, the row id matches the row entity id, so each row
 * group is resolved against the nodes below the list node. Nested lists are
 * traversed recursively, rows no longer present in the form are skipped.
 */
function applyValidationNode(node: FormNode | null, validation: ValidationNode) {
  if (!node) {
    return
  }

  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  applyMessages(node, validation.messages ?? [])

  const rows = validation.rows ?? []
  rows.forEach((row) => {
    const rowNode = findNode(node, row.id)
    if (!rowNode) {
      return
    }

    const rowFields = Object.keys(row.items)
    rowFields.forEach((f) => {
      applyValidationNode(findNode(rowNode, f), row.items[f])
    })
  })
}

function applyMessages(node: FormNode, messages: Message[]) {
  if (!messages.length) {
    return
  }

  const toAdd = messages.map((m) => m.code)
  let params = (messages.map((m) => m.params).flat() as string[]) ?? []
  const messageParams: ValidationMessageParameters[] = []
  messages.forEach((m) => {
    const message = readMessageModelByCode(m.code)
    const paramToAdd = extractParameters(message?.description as string, m.description) ?? []
    params = [...params, ...paramToAdd].filter((p) => !!p)
    messageParams.push({
      code: m.code,
      params: [...(m.params ?? []), ...paramToAdd].filter((p) => !!p),
    })
  })

  /**
   * the server side validation returns the error message with the description already
   * interpolated and traslated, given how the form works client side cicle trough the
   * server messages and extract the parameters that where used, so we can treat server
   * messages like any other message without the need of special handling inside the form
   */
  const current = node.value.validationMessages ?? []
  node.value.validationMessages = current.concat(toAdd)
  node.value.validationParams = params ?? []
  node.value.validationMessageParams = [...(node.value.validationMessageParams ?? []), ...messageParams]
  node.value.validationRevealed = true
}

/**
 * Given a template string and the result of the string.format this method
 * will extract the parameters that where used to format that string
 * @param template the template with the placeholders
 * @param formattedString the result of the string.format
 * @returns the parameters that wher used in the string.format
 */
function extractParameters(template: string, formattedString: string) {
  /**
   * the message model could be missing from the design model, in that case there
   * is no template to extrapolate the parameters from
   */
  if (!template) {
    return null
  }

  // Escape special characters in the template and replace placeholders with regex patterns
  let regexString = template.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&') // Escape special characters
  regexString = regexString.replace(/\\\{[0-9]+\\\}/g, '(.*)') // Replace placeholders

  // Create a RegExp object
  const regex = new RegExp(regexString)

  // Match the formatted string against the regex
  const match = formattedString.match(regex)

  // Extract captured groups if match is found
  if (match) {
    return match.slice(1) // Returns an array of captured groups
  }

  return null // No match found
}
